import * as React from 'react';

import {PageContentTwoColumn} from 'shells/layout/page-content-two-column';
import {SavedSettingToast} from 'shells/saved-setting-toast/saved-setting-toast';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {PhoneMessagePreview} from 'shells/sms-campaigns/message-preview/phone-message-preview';
import {SectionLabel} from 'shells/typography/section-label';
import {useDebounce} from 'shells/utils/hooks/use-debounce';
import {useDidMountEffect} from 'shells/utils/hooks/use-did-mount-effect';

import type {
    SmsDripEditionFragment as Edition,
    SmsEditionUpdateInput,
} from 'nutshell-graphql-types';

import {EditionDetailsForm} from './edition-details-form';

type Props = {
    edition: Edition;
    onUpdate: (input: SmsEditionUpdateInput) => Promise<any>;
};

export function EditionDetails(props: Props) {
    const {
        edition: {id: editionId, status: editionStatus},
    } = props;

    const [content, setContent] = React.useState(props.edition.content);
    const [isSavingBroadcastDetails, setIsSavingBroadcastDetails] = React.useState(false);

    const {toastState} = useManageSaveState(isSavingBroadcastDetails, 1000);
    const debouncedContent = useDebounce(content, 300);

    const handleUpdate = (input: SmsEditionUpdateInput) => {
        setIsSavingBroadcastDetails(true);

        props
            .onUpdate(input)
            .then(() => {
                setIsSavingBroadcastDetails(false);
            })
            .catch(() => {
                setIsSavingBroadcastDetails(false);
            });
    };

    const onSaveButtonClick = () => {
        handleUpdate({editionId: props.edition.id, content: debouncedContent});
    };

    useDidMountEffect(() => {
        if (editionStatus !== 'SENDING') {
            handleUpdate({editionId: editionId, content: debouncedContent});
        }
    }, [debouncedContent, editionId, editionStatus]);

    return (
        <PageContentTwoColumn
            withoutOuterPadding={true}
            leftContent={
                <>
                    <SectionLabel>Preview</SectionLabel>
                    <div className='full-width full-height background-offwhite flex align-center justify-center'>
                        <PhoneMessagePreview
                            message={props.edition.content}
                            optOutText={props.edition.optOutText || undefined}
                        />
                    </div>
                </>
            }
            rightContent={
                <>
                    <SectionLabel>
                        <SavedSettingToast state={toastState}>Message details</SavedSettingToast>
                    </SectionLabel>
                    <EditionDetailsForm
                        content={content}
                        edition={props.edition}
                        onContentChange={setContent}
                        isSaving={isSavingBroadcastDetails}
                        editionStatus={props.edition.status}
                        onSaveButtonClick={onSaveButtonClick}
                        isFirstEdition={props.edition.isFirstEdition}
                        onUpdate={handleUpdate}
                    />
                </>
            }
        />
    );
}
